import classNames from './LoginViewContent.module.scss';
import classes from 'classnames';
import SVG from 'react-inlinesvg';
import { useTranslation } from 'react-i18next';
import UseScreenSize from '@/hooks/UseScreenSize';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Transition } from 'react-transition-group';

import TheButton from '@/components/TheButton/TheButton';
import LoginForm from '@/components/LoginForm/LoginForm';
import QuickLogin from '@/components/QuickLogin/QuickLogin';
import AppBackdrop from '@/components/AppBackdrop';
import AppModal from '@/components/AppModal';

import leftArrow from '@/assets/svg/left-arrow.svg';
import welcome from '@/assets/svg/login_banner.svg';
import welcomeDesktop from '@/assets/svg/welcome-desktop.svg';
import logo from '@/assets/svg/logo-big.svg';
import { useAppSelector, useAppDispatch } from '@/store';
import { authSelector, authActions } from '@/store/reducers/auth';
import { clearError, uiSelector } from '@/store/reducers/ui';
import UseLogin from '@/pages/login/UseLogin';
import { LoginStep } from '@/types/login';
import GoogleLoginError, {
  GoogleLoginErrorTypes,
} from '../LoginForm/GoogleLoginError/GoogleLoginError';
import ClassCodePasswordRequested from '../LoginForm/ClassCodePasswordRequested';
import Footer from '../Footer/Footer';
import DealLimitReachedMessage from '../DealLimitReachedMessage/DealLimitReachedMessage';
import StoreButtons from '@/components/StoreButtons/StoreButtons';
import ClassCodeError from '../LoginForm/ClassCodeError';
import commonUtils from '@/utils/common';
import userUtils from '@/utils/user';

const LoginViewContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    isFromRegistration,
    missingGooglePermissions,
    nativeLanguage,
    getUserInfoError,
    dealExpiredRole,
  } = useAppSelector(authSelector);
  const { showErrorModal } = useAppSelector(uiSelector);
  const useLoginData = UseLogin();
  const { loginError, setLoginError, passwordRequested, setLoading } = useLoginData;

  const { isMobile } = UseScreenSize();

  const [showForm, setShowForm] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showInstallAppScreen, setShowInstallAppScreen] = useState(false);
  const isFirstTime = true;

  const onQuickLogin = () => {};

  useEffect(() => {
    if (isFromRegistration) {
      setShowForm(true);
    }

    document
      .querySelector('meta[name="viewport"]')
      ?.setAttribute(
        'content',
        'width=device-width, initial-scale=1, interactive-widget=resizes-content'
      );

    return () => {
      document
        .querySelector('meta[name="viewport"]')
        ?.setAttribute('content', 'width=device-width, initial-scale=1');
    };
  }, []);

  useEffect(() => {
    if (
      useLoginData.currentLoginStep?.type === LoginStep.CLASS_CODE ||
      useLoginData.currentLoginStep?.type === LoginStep.FORGOT_PASSWORD ||
      useLoginData.currentLoginStep?.type === LoginStep.COGNITO_PASSWORD_CHANGE ||
      useLoginData.currentLoginStep?.type === LoginStep.CLASS_CODE_REGISTRATION ||
      useLoginData.currentLoginStep?.type === LoginStep.AVATAR_UPDATE ||
      useLoginData.currentLoginStep?.type === LoginStep.CLASS_CODE_ERROR
    ) {
      setShowWelcome(false);
    } else {
      setShowWelcome(true);
    }
  }, [useLoginData.currentLoginStep]);

  const clearErrors = useCallback(() => {
    setLoginError(null);
    dispatch(clearError());
    dispatch(authActions.clearMissingGooglePermissions());
    setLoading(false);
  }, [setLoginError, dispatch]);

  useEffect(() => {
    switch (getUserInfoError) {
      case '':
        break;
      case 'GooglePermissions':
        break;
      case 'DealHasExpired':
        navigate('/deal-error-message', {
          state: {
            title: t('registrationDealExpiredMessageTitle'),
            description: t(userUtils.getDealExpiredDescriptionKey(dealExpiredRole)),
            btnText: t('back'),
          },
        });
        setTimeout(() => {
          clearErrors();
        }, 500);
        break;
    }
  }, [t, getUserInfoError, dealExpiredRole, clearErrors, navigate]);

  const onGoogleLoginErrorCloseBtnClick = useCallback(() => {
    clearErrors();
    navigate('/login?provider=google');
  }, [clearErrors, navigate]);

  // Helper function to check if user has Google token
  const checkHasGoogleToken = useCallback(() => {
    const googleToken = localStorage.getItem('X-google-token');
    return googleToken && googleToken !== 'undefined' && googleToken !== 'null';
  }, []);

  useEffect(() => {
    if (loginError && !checkHasGoogleToken()) {
      commonUtils.showGlobalError({
        i18nKey: 'error.somethingWentWrong',
      });
      setLoginError(null);
      setLoading(false); // Clear loading state when showing error
    }
  }, [loginError, checkHasGoogleToken, setLoginError, setLoading]);

  useEffect(() => {
    if (showErrorModal) {
      setLoading(false);
    }
  }, [showErrorModal, setLoading]);

  const googleLoginErrorType: GoogleLoginErrorTypes | null = (() => {
    if (missingGooglePermissions && missingGooglePermissions.length > 0) {
      return GoogleLoginErrorTypes.missing_google_permissions;
    }

    if (loginError === 'Unauthorized') {
      return GoogleLoginErrorTypes.unauthorized;
    }

    // Only show Google error if user has Google token
    if (loginError && checkHasGoogleToken()) {
      return GoogleLoginErrorTypes.default;
    }

    return null;
  })();

  const name = 'Sarah Jonas';
  const level = 'Foundation';
  const grade = 9;

  const onContinueOnWeb = () => {
    setShowInstallAppScreen(false);
    setShowForm(true);
  };

  const closeInstallAppScreen = () => {
    setShowInstallAppScreen(false);
  };

  const bottom = isFirstTime ? (
    <TheButton
      className={classNames.newNav}
      shouldRecolor={false}
      text={t('getStarted')}
      emitOnClick={() => {
        if (isMobile && nativeLanguage !== 'he') {
          setShowInstallAppScreen(true);
        } else {
          setShowForm(true);
        }
      }}
    />
  ) : (
    <div className='flex flex-col gap-6 w-full'>
      <QuickLogin name={name} level={level} grade={grade} emitQuickLogin={onQuickLogin} />
      <button className={classNames.oldNav} onClick={() => setShowForm(true)}>
        {t('loginFromDifferent')}
      </button>
    </div>
  );

  const text = (
    <div className={classNames.text}>
      <div className={classNames.logo}>
        <SVG src={logo} />
      </div>
      <p>{!isFirstTime && isMobile ? t('pleaseSelectYourAccount') : t('exploreEngaging')}</p>
      {!isMobile ? <StoreButtons /> : ''}
    </div>
  );

  const isMainLoginView =
    useLoginData.currentLoginStep?.type === LoginStep.LOGIN ||
    useLoginData.currentLoginStep?.type === LoginStep.MG_LOGIN;

  const loginForm = (
    <LoginForm useLoginData={useLoginData} hideWelcome={() => setShowWelcome(false)} />
  );

  const installAppScreen = (
    <Transition
      in={showInstallAppScreen}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop
              emitClose={closeInstallAppScreen}
              className={classes(classNames.installAppScreenBackdrop, `backdrop-${state}`)}
            />
            <AppModal className={classNames.installAppScreenModal}>
              <div className={classes(classNames.installAppScreen, `slide-vertical-${state}`)}>
                <div className={classNames.grayPass} onClick={closeInstallAppScreen}></div>
                <h2>{t('getFullExperience')}</h2>
                <StoreButtons />
                <button className={classNames.continueWebBtn} onClick={onContinueOnWeb}>
                  {t('continueOnWeb')}
                </button>
              </div>
            </AppModal>
          </>
        );
      }}
    />
  );

  let content = (
    <>
      {!isMainLoginView && (
        <button
          className={classNames.backArrow}
          onClick={() => {
            useLoginData.restartLogin();
          }}
        >
          {t('tutorBack')}
        </button>
      )}
      {useLoginData.currentLoginStep?.type !== LoginStep.CLASS_CODE_ERROR && (
        <>
          <div className={classNames.mainContaner}>
            <div
              className={classes({
                [classNames.avatarModal]:
                  useLoginData.currentLoginStep?.type === LoginStep.AVATAR_UPDATE,
                [classNames.inner]: useLoginData.currentLoginStep?.type !== LoginStep.AVATAR_UPDATE,
                [classNames.showWelcomeImg]: showWelcome,
              })}
            >
              {showWelcome && (
                <div className={classNames.innerWelcome}>
                  <SVG className={classNames.welcomeImg} src={welcomeDesktop} />
                  {text}
                </div>
              )}

              {googleLoginErrorType ? (
                <GoogleLoginError
                  errorType={googleLoginErrorType}
                  onCloseBtnClick={onGoogleLoginErrorCloseBtnClick}
                />
              ) : passwordRequested ? (
                <ClassCodePasswordRequested onSubmit={useLoginData.OnCompleteForgotPassword} />
              ) : (
                loginForm
              )}
            </div>
          </div>
          <Footer fromLogin={true} />
        </>
      )}
      {useLoginData.currentLoginStep?.type === LoginStep.CLASS_CODE_ERROR && (
        <ClassCodeError classCodeErrorObject={useLoginData?.classCodeErrorObject} />
      )}
    </>
  );

  if (isMobile) {
    content =
      showForm || !isMainLoginView ? (
        <>
          <div className={classNames.backBtn}>
            <button
              onClick={() => {
                useLoginData.restartLogin();
                setShowWelcome(true);
                setShowInstallAppScreen(false);
                setShowForm(false);
              }}
            >
              <SVG src={leftArrow} />
            </button>
          </div>
          {googleLoginErrorType ? (
            <GoogleLoginError
              errorType={googleLoginErrorType}
              onCloseBtnClick={onGoogleLoginErrorCloseBtnClick}
            />
          ) : (
            loginForm
          )}
        </>
      ) : (
        <>
          <div className={classNames.mobileInner}>
            <SVG className={classNames.welcomeImg} src={welcome} />
            {text}
            {bottom}
            {installAppScreen}
          </div>
        </>
      );
  }

  return (
    <>
      <DealLimitReachedMessage
        show={useLoginData.showDealLimitReachedModal}
        onClose={useLoginData.onCloseDealLimitReachedModal}
      />
      <div
        className={classes(classNames.loginViewContent, {
          [classNames.loginViewContentMobile]: isMobile,
        })}
      >
        {content}
      </div>
    </>
  );
};

export default LoginViewContent;
